import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { buildApiUrl } from '../utils/api/config';
import { newsAPI } from '../utils/api/newsAPI';
import './AdminNewsAdd.css';

function AdminNewsAdd() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [savedNews, setSavedNews] = useState([]);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    headline: '',
    subHeadline: '',
    description: '',
    content: '',
    category: 'local',
    location: '',
    featuredImage: null,
    images: [],
    tags: '',
    isPublished: true
  });

  const categories = ['politics', 'sports', 'technology', 'entertainment', 'local', 'other'];

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const refreshNewsList = useCallback(async () => {
    try {
      setListLoading(true);
      const response = await newsAPI.getAdminNews({ limit: 100 });
      if (response.success) {
        setSavedNews(response.data || []);
      }
    } catch (error) {
      showToast('❌ News list load nahi ho saki', 'error');
    } finally {
      setListLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    refreshNewsList();
  }, [refreshNewsList]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        featuredImage: file
      }));
    }
  };

  const getImagePreview = () => {
    if (formData.featuredImage instanceof File) {
      return URL.createObjectURL(formData.featuredImage);
    }
    return typeof formData.featuredImage === 'string' ? formData.featuredImage : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.headline || !formData.description || !formData.content) {
      showToast('❌ Please fill all required fields', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('title', formData.headline);
      submitData.append('shortDescription', formData.description);
      submitData.append('description', formData.content);
      submitData.append('category', formData.category);
      submitData.append('subCategory', formData.location);
      submitData.append('isPublished', formData.isPublished ? 'true' : 'false');
      
      // Add tags as array
      const tagsList = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      tagsList.forEach(tag => {
        submitData.append('tags', tag);
      });
      
      // Add image file if selected
      if (formData.featuredImage instanceof File) {
        submitData.append('featuredImage', formData.featuredImage);
      } else {
        showToast('❌ Please select an image', 'error');
        setLoading(false);
        return;
      }

      const response = await fetch(buildApiUrl('/news/add'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser will set it with boundary for multipart
        },
        body: submitData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add news');
      }

      showToast('✅ News added successfully!');
      setFormData({
        headline: '',
        subHeadline: '',
        description: '',
        content: '',
        category: 'local',
        location: '',
        featuredImage: null,
        images: [],
        tags: '',
        isPublished: true
      });
      await refreshNewsList();
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('क्या आप यह समाचार हटाना चाहते हैं?')) {
      return;
    }

    try {
      const response = await newsAPI.deleteNews(id);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete news');
      }
      showToast('✅ News deleted successfully!');
      await refreshNewsList();
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  return (
    <>
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <AdminShell
        eyebrow="Editorial Publishing"
        title="Add news story"
        description="Create and publish a fresh article with headline, media, category details, and tags from a cleaner editorial workflow."
        primaryAction={{ label: 'Refresh list', onClick: refreshNewsList }}
        secondaryAction={{ label: 'Back to dashboard', onClick: handleCancel }}
        meta={[
          { label: 'Categories', value: String(categories.length) },
          { label: 'Publish mode', value: formData.isPublished ? 'Live' : 'Draft' },
          { label: 'Saved items', value: String(savedNews.length) }
        ]}
      >
        <div className="admin-news-add-container">
          <div className="news-add-wrapper">
            <form onSubmit={handleSubmit} className="news-add-form">
          {/* Headline Section */}
          <div className="form-section">
            <h3>📝 समाचार शीर्षक</h3>
            
            <div className="form-group">
              <label>Headline (मुख्य शीर्षक) *</label>
              <input
                type="text"
                name="headline"
                value={formData.headline}
                onChange={handleInputChange}
                placeholder="मुख्य शीर्षक लिखें..."
                maxLength={200}
                required
              />
              <small>{formData.headline.length}/200</small>
            </div>

            <div className="form-group">
              <label>Sub-Headline (सहायक शीर्षक)</label>
              <input
                type="text"
                name="subHeadline"
                value={formData.subHeadline}
                onChange={handleInputChange}
                placeholder="सहायक शीर्षक (वैकल्पिक)"
                maxLength={200}
              />
              <small>{formData.subHeadline.length}/200</small>
            </div>
          </div>

          {/* Content Section */}
          <div className="form-section">
            <h3>📄 समाचार विवरण</h3>
            
            <div className="form-group">
              <label>Short Description (संक्षिप्त विवरण) *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="संक्षिप्त विवरण लिखें (2-3 लाइनें)"
                rows={3}
                maxLength={500}
                required
              />
              <small>{formData.description.length}/500</small>
            </div>

            <div className="form-group">
              <label>Full Content (पूरी खबर) *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="पूरी खबर यहाँ विस्तार से लिखें..."
                rows={8}
                required
              />
              <small>{formData.content.length} characters</small>
            </div>
          </div>

          {/* Media Section */}
          <div className="form-section">
            <h3>🖼️ मीडिया</h3>
            
            <div className="form-group">
              <label>Featured Image (मुख्य फोटो) *</label>
              <input
                type="file"
                name="featuredImage"
                onChange={handleImageChange}
                accept="image/*"
                required
              />
              <small>JPG, PNG, GIF, WebP - Max 10MB</small>
              {getImagePreview() && (
                <div className="image-preview">
                  <img src={getImagePreview()} alt="preview" />
                </div>
              )}
            </div>
          </div>

          {/* Category & Location Section */}
          <div className="form-section">
            <h3>🏷️ विभाग और स्थान</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Category (विभाग) *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Location (स्थान)</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="चित्रकूट, उ.प्र."
                />
              </div>
            </div>
          </div>

          {/* Tags Section */}
          <div className="form-section">
            <h3>🏷️ टैग्स</h3>
            
            <div className="form-group">
              <label>Tags (विषय टैग) - अल्पविराम से अलग करें</label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="उदा: राजनीति, चित्रकूट, समाचार"
              />
              <small>कई टैग्स जोड़ने के लिए अल्पविराम (,) से अलग करें</small>
            </div>
          </div>

          {/* Publishing Section */}
          <div className="form-section">
            <h3>📤 प्रकाशन</h3>
            
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  name="isPublished"
                  checked={formData.isPublished}
                  onChange={handleInputChange}
                />
                तुरंत प्रकाशित करें
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? '⏳ प्रकाशित हो रही है...' : '✅ समाचार प्रकाशित करें'}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
              disabled={loading}
            >
              ✖️ रद्द करें
            </button>
          </div>
            </form>

            <section className="saved-news-section">
              <div className="saved-news-section-header">
                <h3>Saved News</h3>
              </div>

              {listLoading ? (
                <div className="saved-news-empty">Loading...</div>
              ) : savedNews.length === 0 ? (
                <div className="saved-news-empty">Abhi tak koi news add nahi hui.</div>
              ) : (
                <div className="saved-news-list">
                  {savedNews.map((item) => (
                    <article key={item._id} className="saved-news-item">
                      <div className="saved-news-copy">
                        <h4>{item.title}</h4>
                        <p>{item.categoryName || item.category}</p>
                        <small>{new Date(item.createdAt).toLocaleDateString('hi-IN')}</small>
                      </div>
                      <div className="saved-news-actions">
                        <button type="button" className="btn-cancel" onClick={() => navigate(`/admin/news/edit/${item._id}`)}>
                          Edit
                        </button>
                        <button type="button" className="btn-submit danger" onClick={() => handleDelete(item._id)}>
                          Delete
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </AdminShell>
    </>
  );
}

export default AdminNewsAdd;
